import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import Select from '../../../../../../components/input/Select/Select';
import Button from '../../../../../../components/Button';
import WidgetLayoutSelectModal from './WidgetLayoutSelectModal';
import { WIDGET_LAYOUT_V2, WIDGET_TYPE_V2 } from '../../../../../../types/widget';
import {
  BANNER_WIDGET_LAYOUT_TYPE_IMG_V2,
  BANNER_WIDGET_LAYOUT_TYPE_LABEL_V2,
  BANNER_WIDGET_LAYOUT_TYPE_SELECT_LIST,
  BRAND_WIDGET_LAYOUT_TYPE_IMG_V2,
  BRAND_WIDGET_LAYOUT_TYPE_LABEL_V2,
  BRAND_WIDGET_LAYOUT_TYPE_SELECT_LIST,
  EVENT_WIDGET_LAYOUT_TYPE_IMG_V2,
  EVENT_WIDGET_LAYOUT_TYPE_LABEL_V2,
  EVENT_WIDGET_LAYOUT_TYPE_SELECT_LIST,
  PRODUCT_WIDGET_LAYOUT_TYPE_IMG_V2,
  PRODUCT_WIDGET_LAYOUT_TYPE_LABEL_V2,
  PRODUCT_WIDGET_LAYOUT_TYPE_SELECT_LIST,
  TIMEDEAL_WIDGET_LAYOUT_TYPE_IMG_V2,
  TIMEDEAL_WIDGET_LAYOUT_TYPE_LABEL_V2,
  TIMEDEAL_WIDGET_LAYOUT_TYPE_SELECT_LIST,
  WIDGET_TYPE_LABEL_V2,
} from '../../../../../../constants/service/app/widgetConstantsV2';
import { WidgetLayoutSelectStyle } from './style';
import { asyncSimpleModal } from '../../../../../../lib/common';

const WIDGET_LAYOUT_TYPE_SELECT_LIST_V2 = {
  [WIDGET_TYPE_V2.PRODUCT]: PRODUCT_WIDGET_LAYOUT_TYPE_SELECT_LIST,
  [WIDGET_TYPE_V2.BRAND]: BRAND_WIDGET_LAYOUT_TYPE_SELECT_LIST,
  [WIDGET_TYPE_V2.EVENT]: EVENT_WIDGET_LAYOUT_TYPE_SELECT_LIST,
  [WIDGET_TYPE_V2.BANNER]: BANNER_WIDGET_LAYOUT_TYPE_SELECT_LIST,
  [WIDGET_TYPE_V2.TIMEDEAL]: TIMEDEAL_WIDGET_LAYOUT_TYPE_SELECT_LIST,
};

const WIDGET_LAYOUT_TYPE_IMG_V2: Record<WIDGET_TYPE_V2, { [key: string]: string }> = {
  [WIDGET_TYPE_V2.PRODUCT]: PRODUCT_WIDGET_LAYOUT_TYPE_IMG_V2,
  [WIDGET_TYPE_V2.BRAND]: BRAND_WIDGET_LAYOUT_TYPE_IMG_V2,
  [WIDGET_TYPE_V2.EVENT]: EVENT_WIDGET_LAYOUT_TYPE_IMG_V2,
  [WIDGET_TYPE_V2.BANNER]: BANNER_WIDGET_LAYOUT_TYPE_IMG_V2,
  [WIDGET_TYPE_V2.TIMEDEAL]: TIMEDEAL_WIDGET_LAYOUT_TYPE_IMG_V2,
};

const WIDGET_LAYOUT_TYPE_LABEL_V2: Record<WIDGET_TYPE_V2, { [key: string]: string }> = {
  [WIDGET_TYPE_V2.PRODUCT]: PRODUCT_WIDGET_LAYOUT_TYPE_LABEL_V2,
  [WIDGET_TYPE_V2.BRAND]: BRAND_WIDGET_LAYOUT_TYPE_LABEL_V2,
  [WIDGET_TYPE_V2.EVENT]: EVENT_WIDGET_LAYOUT_TYPE_LABEL_V2,
  [WIDGET_TYPE_V2.BANNER]: BANNER_WIDGET_LAYOUT_TYPE_LABEL_V2,
  [WIDGET_TYPE_V2.TIMEDEAL]: TIMEDEAL_WIDGET_LAYOUT_TYPE_LABEL_V2,
};

interface WidgetLayoutSelectProps {
  type: WIDGET_TYPE_V2;
  value?: WIDGET_LAYOUT_V2;
  onChange?: (value: WIDGET_LAYOUT_V2) => void;
  checkValidSelect?: (type: WIDGET_LAYOUT_V2) => string | null;
}

const WidgetLayoutSelect = forwardRef((props: WidgetLayoutSelectProps, ref) => {
  const { type, value, checkValidSelect, onChange } = props;
  const list = WIDGET_LAYOUT_TYPE_SELECT_LIST_V2[type];
  const initialValue = value || list[0].id;
  const [currentLayoutType, setCurrentLayoutType] = useState(initialValue);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isFirstItem = currentLayoutType === list[0].id;
  const isLastItem = currentLayoutType === list[list.length - 1].id;
  const isChangeType = value ? value !== currentLayoutType : initialValue !== currentLayoutType;

  useImperativeHandle(ref, () => ({
    value,
    onChange,
  }));

  useEffect(() => {
    if (value) setCurrentLayoutType(value);
  }, [value]);

  const handleChange = async (value: WIDGET_LAYOUT_V2): Promise<void> => {
    const errMessage = checkValidSelect && checkValidSelect(value);
    if (errMessage) {
      await asyncSimpleModal({
        type: 'error',
        title: '아이템 개수 초과',
        message: errMessage,
      });
      return;
    }

    onChange && onChange(value);
  };

  const handleClickPreviewButton = (): void => setIsModalOpen(true);

  const handleOk = (): void => {
    setIsModalOpen(false);
    handleChange(currentLayoutType);
  };

  const handleClose = (): void => {
    setIsModalOpen(false);
    //초기값 리셋
    setCurrentLayoutType(initialValue);
  };

  const handleNext = (): void => {
    const currentLayoutIndex = list.findIndex((item) => item.id === currentLayoutType);
    setCurrentLayoutType(list[currentLayoutIndex + 1].id);
  };

  const handlePrev = (): void => {
    const currentLayoutIndex = list.findIndex((item) => item.id === currentLayoutType);
    setCurrentLayoutType(list[currentLayoutIndex - 1].id);
  };

  const img = WIDGET_LAYOUT_TYPE_IMG_V2[type][currentLayoutType];

  return (
    <WidgetLayoutSelectStyle>
      <Select list={list} onChange={handleChange} value={value} isTable={false} style={{ width: 260 }} />
      <Button color="primary" text="위젯 모양 확인하기" onClick={handleClickPreviewButton} type="button" />
      {isModalOpen && (
        <WidgetLayoutSelectModal
          title={`${WIDGET_TYPE_LABEL_V2[type]} - ${WIDGET_LAYOUT_TYPE_LABEL_V2[type][currentLayoutType]}`}
          img={img}
          isFirstItem={isFirstItem}
          isLastItem={isLastItem}
          okText={isChangeType ? '해당 레이아웃 타입으로 변경' : '확인'}
          onOk={handleOk}
          onClose={handleClose}
          onNext={handleNext}
          onPrev={handlePrev}
        />
      )}
    </WidgetLayoutSelectStyle>
  );
});

WidgetLayoutSelect.displayName = 'WidgetLayoutSelect';

export default WidgetLayoutSelect;
